import {Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {Router} from '@angular/router';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {environment} from '../../../environments/environment';

declare const google: any;

type LoginStep = 'credentials' | 'setup' | 'verify';

interface FirstFactorResponse {
  status: 'ok' | '2fa_required';
  step?: 'setup' | 'verify';
  can_skip?: boolean;
}

interface TwoFactorStatus extends Omit<FirstFactorResponse, 'status'> {
  pending: boolean;
  email?: string;
}

interface TwoFactorProvisioning {
  qr_code: string;
  secret: string;
  uri: string;
}

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
  form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', Validators.required],
  });
  // Second factor: a 6-digit authenticator code, optionally typed with a space in the middle.
  code = new FormControl('', [Validators.required, Validators.pattern(/^\d{3}\s?\d{3}$/)]);
  step: LoginStep = 'credentials';
  canSkip = false;
  pendingEmail: string = null;
  provisioning: TwoFactorProvisioning = null;
  error: string = null;
  loading = false;
  checking = true;
  googleUnavailable = false;
  private timers: any[] = [];

  constructor(private fb: FormBuilder, private http: HttpClient, private router: Router, private zone: NgZone) {}

  ngOnInit() {
    this.http.get('rest-api/v1/admin/task-management/admins/me').subscribe({
      next: () => this.router.navigate(['/admin']),
      error: () => this.resumePendingLogin(),
    });
  }

  ngOnDestroy() {
    this.timers.forEach(clearTimeout);
    this.timers = [];
  }

  // A login parked between the first and second factor (e.g. the Facebook redirect flow lands here) is
  // picked up from the server so the user continues at the right step instead of seeing the password form.
  private resumePendingLogin() {
    this.http.get<TwoFactorStatus>('/rest-api/dashboard/2fa/status').subscribe({
      next: status => {
        this.checking = false;
        if (status.pending) {
          this.pendingEmail = status.email;
          this.enterSecondFactor(status);
        } else {
          this.showCredentials();
        }
      },
      error: () => {
        this.checking = false;
        this.showCredentials();
      },
    });
  }

  private showCredentials() {
    this.step = 'credentials';
    this.provisioning = null;
    this.code.reset('');
    // The GSI container is re-rendered with the credentials form, so (re)attach the Google button.
    this.timers.push(setTimeout(() => this.initGoogle()));
  }

  private initGoogle(attempt = 0) {
    if (typeof google === 'undefined' || !google.accounts?.id) {
      if (attempt < 20) {
        this.timers.push(setTimeout(() => this.initGoogle(attempt + 1), 150));
      } else {
        this.googleUnavailable = true;
      }
      return;
    }
    google.accounts.id.initialize({
      client_id: environment.googleClientId,
      callback: (resp: any) => this.onGoogleCredential(resp),
    });
    const el = document.getElementById('google-btn');
    if (el) {
      // This GSI button is invisible (see .google-btn__gsi) and only exists to capture the
      // click that yields the id_token — our own .btn-google face is what the user sees.
      // Render it wide so, once scaled up in CSS, it fully covers the visible button area.
      google.accounts.id.renderButton(el, {theme: 'filled_blue', size: 'large', width: 400});
    }
  }

  private onGoogleCredential(resp: any) {
    this.zone.run(() => {
      this.loading = true;
      this.error = null;
      this.http.post<FirstFactorResponse>('/rest-api/dashboard/google_login', {id_token: resp.credential}).subscribe({
        next: result => this.onFirstFactorPassed(result),
        error: err => {
          this.loading = false;
          this.error = err.error?.error || 'Google login failed';
        },
      });
    });
  }

  submit() {
    if (this.form.invalid) return;
    this.loading = true;
    this.error = null;
    const body = new HttpParams()
      .set('email', this.form.value.email)
      .set('password', this.form.value.password);
    this.http
      .post<FirstFactorResponse>('/rest-api/dashboard/login', body.toString(), {
        headers: {'Content-Type': 'application/x-www-form-urlencoded'},
      })
      .subscribe({
        next: result => this.onFirstFactorPassed(result),
        error: err => {
          this.loading = false;
          this.error = err.error?.error || 'Incorrect email or password';
        },
      });
  }

  loginWithFacebook() {
    window.location.href = '/rest-api/v1/companies/facebook_user_login_redirect?redirect_to_admin=1';
  }

  private onFirstFactorPassed(result: FirstFactorResponse) {
    if (result.status === '2fa_required') {
      this.pendingEmail = this.form.value.email || null;
      this.enterSecondFactor(result);
      return;
    }
    this.router.navigate(['/admin']);
  }

  private enterSecondFactor(state: {step?: 'setup' | 'verify'; can_skip?: boolean}) {
    this.error = null;
    this.canSkip = !!state.can_skip;
    this.code.reset('');
    if (state.step === 'setup') {
      this.loadProvisioning();
    } else {
      this.loading = false;
      this.step = 'verify';
    }
  }

  private loadProvisioning() {
    this.loading = true;
    this.http.post<TwoFactorProvisioning>('/rest-api/dashboard/2fa/setup', {}).subscribe({
      next: provisioning => {
        this.provisioning = provisioning;
        this.step = 'setup';
        this.loading = false;
      },
      error: err => this.onSecondFactorError(err, 'Could not start the authenticator setup'),
    });
  }

  verify() {
    if (this.code.invalid || this.loading) return;
    this.loading = true;
    this.error = null;
    this.http.post('/rest-api/dashboard/2fa/verify', {code: this.code.value.replace(/\s/g, '')}).subscribe({
      next: () => this.router.navigate(['/admin']),
      error: err => this.onSecondFactorError(err, 'Incorrect code'),
    });
  }

  skipSetup() {
    if (this.loading) return;
    this.loading = true;
    this.error = null;
    this.http.post('/rest-api/dashboard/2fa/skip', {}).subscribe({
      next: () => this.router.navigate(['/admin']),
      error: err => this.onSecondFactorError(err, 'Authenticator setup is required'),
    });
  }

  private onSecondFactorError(err: any, fallback: string) {
    this.loading = false;
    this.error = err.error?.error || fallback;
    // 401 = the pending login expired (or was locked out): start over from the credentials form.
    if (err.status === 401 || this.error === 'Too many login attempts') {
      this.showCredentials();
    }
  }

  // Abandon the second factor; the server-side pending login is dropped together with the session.
  backToLogin() {
    this.loading = true;
    this.http.post('/rest-api/dashboard/logout', {}).subscribe({
      complete: () => {
        this.loading = false;
        this.error = null;
        this.showCredentials();
      },
    });
  }
}
